import { ComponentType } from 'react';
import { SvgIconProps } from '@mui/material';
import DashboardRoundedIcon from '@mui/icons-material/DashboardRounded';
import BarChartRoundedIcon from '@mui/icons-material/BarChartRounded';
import PeopleAltRoundedIcon from '@mui/icons-material/PeopleAltRounded';
import StorefrontRoundedIcon from '@mui/icons-material/StorefrontRounded';
import WorkspacePremiumRoundedIcon from '@mui/icons-material/WorkspacePremiumRounded';
import VerifiedRoundedIcon from '@mui/icons-material/VerifiedRounded';
import PaidRoundedIcon from '@mui/icons-material/PaidRounded';
import ChatRoundedIcon from '@mui/icons-material/ChatRounded';
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded';
import AdminPanelSettingsRoundedIcon from '@mui/icons-material/AdminPanelSettingsRounded';
import ManageAccountsRoundedIcon from '@mui/icons-material/ManageAccountsRounded';
import AccountBalanceRoundedIcon from '@mui/icons-material/AccountBalanceRounded';
import Inventory2RoundedIcon from '@mui/icons-material/Inventory2Rounded';
import CategoryRoundedIcon from '@mui/icons-material/CategoryRounded';
import QueryStatsRoundedIcon from '@mui/icons-material/QueryStatsRounded';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import CardMembershipRoundedIcon from '@mui/icons-material/CardMembershipRounded';
import ReceiptRoundedIcon from '@mui/icons-material/ReceiptRounded';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import LocalOfferRoundedIcon from '@mui/icons-material/LocalOfferRounded';
import ScheduleRoundedIcon from '@mui/icons-material/ScheduleRounded';
import { Role } from 'src/Role';

/** Live counters shown next to a menu item. */
export type NavBadge = 'pendingBankTransfers';

export interface NavItem {
  label: string;
  to: string;
  icon: ComponentType<SvgIconProps>;
  badge?: NavBadge;
}

export interface NavSection {
  title: string;
  items: NavItem[];
}

const customersSection: NavSection = {
  title: 'Customers',
  items: [
    { label: 'All users', to: '/management/all-users', icon: PeopleAltRoundedIcon },
    { label: 'All shops', to: '/shops/all-shops', icon: StorefrontRoundedIcon },
    { label: 'Premium shops', to: '/shops/premium-shops', icon: WorkspacePremiumRoundedIcon },
    { label: 'Active premium', to: '/shops/active-premium-shops', icon: VerifiedRoundedIcon },
    { label: 'Paid premium', to: '/shops/paid-premium-shops', icon: PaidRoundedIcon },
    { label: 'Message shops', to: '/shops/message-shops', icon: ChatRoundedIcon }
  ]
};

const operationsSection: NavSection = {
  title: 'Operations',
  items: [
    { label: 'All invoices', to: '/invoice/all-invoices', icon: ReceiptLongRoundedIcon },
    { label: 'Template inventory', to: '/inventory/template-inventory', icon: Inventory2RoundedIcon }
  ]
};

const billingSection: NavSection = {
  title: 'Billing',
  items: [
    { label: 'Bank transfers', to: '/billing/bank-transfers', icon: ReceiptRoundedIcon, badge: 'pendingBankTransfers' },
    { label: 'Subscription plans', to: '/billing/plans', icon: CardMembershipRoundedIcon },
    { label: 'Offers', to: '/billing/offers', icon: LocalOfferRoundedIcon },
    { label: 'Pay later', to: '/billing/pay-later', icon: ScheduleRoundedIcon },
    { label: 'Billing settings', to: '/billing/settings', icon: TuneRoundedIcon }
  ]
};

const administrationSection: NavSection = {
  title: 'Administration',
  items: [
    { label: 'Admin users', to: '/admin/admin-users', icon: ManageAccountsRoundedIcon },
    { label: 'Extended bank payments', to: '/admin/extended-bank-payments', icon: AccountBalanceRoundedIcon },
    { label: 'Roles', to: '/admin/admin-roles', icon: AdminPanelSettingsRoundedIcon }
  ]
};

const platformSections = [customersSection, operationsSection, billingSection, administrationSection];

const NAVIGATION: Partial<Record<Role, NavSection[]>> = {
  [Role.SUPER_ADMIN]: [
    {
      title: 'Overview',
      items: [
        { label: 'Dashboard', to: '/', icon: DashboardRoundedIcon },
        { label: 'Statistics', to: '/management/statistics', icon: BarChartRoundedIcon }
      ]
    },
    ...platformSections
  ],
  [Role.DEVELOPMENT_TEAM]: platformSections,
  [Role.INVENTORY_ADMIN]: [
    {
      title: 'Overview',
      items: [{ label: 'Dashboard', to: '/inventory-admin/dashboard', icon: DashboardRoundedIcon }]
    },
    {
      title: 'Shop',
      items: [
        { label: 'Shop', to: '/inventory-admin/shop', icon: StorefrontRoundedIcon },
        { label: 'Invoices', to: '/inventory-admin/invoices', icon: ReceiptLongRoundedIcon },
        { label: 'Customers', to: '/inventory-admin/customers', icon: GroupsRoundedIcon },
        { label: 'Inventories', to: '/inventory-admin/inventories', icon: Inventory2RoundedIcon },
        { label: 'Categories', to: '/inventory-admin/categories', icon: CategoryRoundedIcon }
      ]
    }
  ],
  [Role.CUSTOMER]: [
    {
      title: 'Overview',
      items: [
        { label: 'Shop', to: '/customer/shop', icon: StorefrontRoundedIcon },
        { label: 'Statistics', to: '/customer/statistics', icon: BarChartRoundedIcon },
        { label: 'Shop stats', to: '/customer/shop-stats', icon: QueryStatsRoundedIcon }
      ]
    },
    {
      title: 'Manage',
      items: [
        { label: 'Invoices', to: '/customer/invoices', icon: ReceiptLongRoundedIcon },
        { label: 'Customers', to: '/customer/customers', icon: GroupsRoundedIcon },
        { label: 'Inventories', to: '/customer/inventories', icon: Inventory2RoundedIcon },
        { label: 'Categories', to: '/customer/categories', icon: CategoryRoundedIcon }
      ]
    }
  ],
  [Role.CUSTOMER_SUPPORT]: [
    {
      title: 'Support',
      items: [
        { label: 'All shops', to: '/customer-support/all-shops', icon: StorefrontRoundedIcon },
        { label: 'Users', to: '/customer-support/admin-users', icon: PeopleAltRoundedIcon },
        { label: 'Bank transfers', to: '/customer-support/bank-transfers', icon: ReceiptRoundedIcon, badge: 'pendingBankTransfers' },
        { label: 'Pay later', to: '/customer-support/pay-later', icon: ScheduleRoundedIcon }
      ]
    }
  ]
};

export function navigationFor(role: string | undefined): NavSection[] {
  return (role && NAVIGATION[role as Role]) || [];
}

/** The menu entry for the current page (longest matching path), with its section. */
export function findNavEntry(
  role: string | undefined,
  pathname: string
): { section: NavSection; item: NavItem } | null {
  let best: { section: NavSection; item: NavItem } | null = null;
  for (const section of navigationFor(role)) {
    for (const item of section.items) {
      const matches =
        item.to === '/' ? pathname === '/' : pathname === item.to || pathname.startsWith(`${item.to}/`);
      if (matches && (!best || item.to.length > best.item.to.length)) best = { section, item };
    }
  }
  return best;
}
